import * as React from 'react';
import moment from 'moment';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { TextInputField } from '../Forms/fields';
import { TaskItemData, TaskItemStatus, TaskItemType } from '../../models';
import { updateTaskStatusAsync } from '../../ajax/requests';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { getTaskTypeTranslationKey } from '../../pages/Tasks/taskUtils';
import { paths } from '../../app/constants';
import AppButton from '../AppButton';

interface TaskCompleteDialogProps {
    task: TaskItemData | null;
    onClose: () => void;
    onCompleted?: () => Promise<void> | void;
}

const TaskDialogTitle = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
`;

const TaskDialogEyebrow = styled.div`
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--brand-secondary);
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const TaskDialogHeadline = styled.div`
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-strong);
    line-height: 1.4;
`;

const TaskDialogMeta = styled.div`
    font-size: 0.82rem;
    color: var(--text-muted);
`;

const TaskCompleteDialog: React.FC<TaskCompleteDialogProps> = ({
    task,
    onClose,
    onCompleted,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const [notes, setNotes] = React.useState('');
    const [isSaving, setSaving] = React.useState(false);

    React.useEffect(() => {
        setNotes(task?.notes ?? '');
    }, [task]);

    const handleClose = () => {
        if (isSaving) return;
        onClose();
    };

    const completeTask = async (): Promise<TaskItemData | null> => {
        if (!task) return null;

        setSaving(true);
        try {
            const updatedTask = await updateTaskStatusAsync(task.id, {
                status: TaskItemStatus.Completed,
                notes: notes.trim() || undefined,
            });
            await onCompleted?.();
            return updatedTask;
        } finally {
            setSaving(false);
        }
    };

    const handleComplete = async () => {
        await completeTask();
        onClose();
    };

    const handleCompleteAndRegisterCommunication = async () => {
        if (!task) return;

        const updatedTask = await completeTask();
        const clientId = updatedTask?.clientId ?? task.clientId;

        if (clientId) {
            dispatch(push({
                pathname: paths.clients(),
                search: `?id=${clientId}&tab=communication`,
            }));
        }
        onClose();
    };

    return (
        <Dialog open={task != null} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <TaskDialogTitle>
                    <TaskDialogEyebrow>{t(TK.completeTask)}</TaskDialogEyebrow>
                    <TaskDialogHeadline>
                        {task?.clientName || t(TK.client)}
                        {task?.title ? ` - ${task.title}` : ''}
                    </TaskDialogHeadline>
                    {task && (
                        <TaskDialogMeta>
                            {t(getTaskTypeTranslationKey(task.type))}
                            {task.dueDate ? ` · ${moment(task.dueDate).format('YYYY-MM-DD HH:mm')}` : ''}
                        </TaskDialogMeta>
                    )}
                </TaskDialogTitle>
            </DialogTitle>
            <DialogContent>
                <div style={{ marginTop: '0.5rem' }}>
                    <TextInputField
                        id="task-complete-notes"
                        type="textarea"
                        label={t(TK.notes)}
                        value={notes}
                        onChange={setNotes}
                        disabled={isSaving}
                        rows={4}
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <AppButton color="light" onClick={handleClose} disabled={isSaving}>
                    {t(TK.cancel)}
                </AppButton>
                {task?.type === TaskItemType.ReminderClientFollowUp && (
                    <AppButton color="primary" onClick={handleCompleteAndRegisterCommunication} disabled={isSaving || !task}>
                        {t(TK.completeAndGoToCommunication)}
                    </AppButton>
                )}
                <AppButton color="success" onClick={handleComplete} disabled={isSaving || !task}>
                    {t(TK.finish)}
                </AppButton>
            </DialogActions>
        </Dialog>
    );
};

export default TaskCompleteDialog;
